import { createStyles, makeUiStyles, Theme } from '../styles/styles';
import { flexCenter } from '../styles/mixins';
import triangleArrowDown from 'common-assets/assets/icons/triangleArrowDownGrey.svg';
import { fontDisplay, fontText, StylesIndexInjections } from 'common-react';

interface FundListStyleProps {
  withActionButton?: boolean;
  isDarkTheme?: boolean;
  hasUnitCategorySelect?: boolean;
  isAccordionExpanded?: boolean;
}

const arrowStyle = {
  content: "''",
  width: 8,
  height: 6,
  display: 'block',
  position: 'absolute',
  backgroundSize: 'cover',
  top: 8,
  left: -12,
  backgroundImage: `url(${triangleArrowDown})`,
};

const ExpandButtonWidth = 64;
const ExpandButtonPaddingLeft = 4;
const AccordionSummaryMaxWidth = `calc(100% - ${ExpandButtonWidth + ExpandButtonPaddingLeft}px)`;

export default makeUiStyles<Theme, FundListStyleProps>((theme: Theme) => createStyles({
  root: {
    marginTop: 16,
  },
  chipBox: {
    [theme.breakpoints.down('sm')]: {
      margin: '-8px 0 24px',
      display: 'flex',
      flexWrap: 'wrap'
    },

    '& > div': {
      margin: '0 8px 8px 0'
    }
  },

  header: {
    marginBottom: 30,
    paddingTop: ({ hasUnitCategorySelect }) => hasUnitCategorySelect ? 0 : 28,
    paddingBottom: 0,

    [theme.breakpoints.down('sm')]: {
      paddingTop: () => 16,
      paddingBottom: 16,
    },
  },
  heading: {
    marginBottom: 24,
  },
  title: {},
  container: {
    width: '100%',

    [theme.breakpoints.down('sm')]: {
      margin: 0,
      minHeight: `calc(100% - ${theme.space.mobileNavHeight()}px)`,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      justifyContent: 'space-between',
    },
  },

  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },

  purchaseList: {
    [theme.breakpoints.down('sm')]: {
      marginBottom: 56
    },

    '& > div': {
      marginBottom: 12
    }
  },

  listSpacer: {
    background: theme.color.greys[15],
    height: 1,
    width: '100%',
    margin: '28px 0',
  },

  fundListFilters: {
    marginBottom: 24
  },

  filterPanel: {
    display: 'flex',
    flexWrap: 'wrap',
  },

  filterPanelMobile: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'space-between',
    },
  },

  modalButtonsBox: {
    backgroundColor: theme.color.greys[0],
    padding: '16px 0',
    ...flexCenter(),
    flexDirection: 'column',

    '& > button:first-of-type': {
      marginBottom: 16
    },

    '& > button:last-of-type': {
      marginBottom: `calc(${theme.space.mobileNavHeight()}px - 24px)`
    }
  },

  dropdownsWrapper: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      display: 'flex',
      flexDirection: 'column'
    },

    '& .MuiAccordionSummary-root': {
      width: '100%',
      cursor: 'initial',
      justifyContent: 'flex-start',
      padding: 0,
      '&.Mui-focused': {
        backgroundColor: 'transparent'
      },
      '&.Mui-focusVisible': {
        outline: 'none',
      },
    },

    '& .MuiAccordionSummary-expandIcon': {
      position: 'relative',
      right: 0,
      marginLeft: ExpandButtonPaddingLeft,
      '&.Mui-expanded': {
        transform: 'rotate(0)',
        '& $chevronDownIcon': {
          transform: 'rotate(180deg)',
        }
      }
    },

    '& .MuiAccordionSummary-root .MuiAccordionSummary-content': {
      width: '100%',
      flexGrow: 0,
      margin: 0,
      '&.Mui-expanded': {
        margin: 0
      }
    },


    '& > div:not($chipBox)': {
      width: '100%',

      [theme.breakpoints.down('sm')]: {
        margin: '0 0 16px'
      }
    },

    '& > div:not($chipBox):not(:last-child)': {
      marginRight: 16,

      [theme.breakpoints.down('sm')]: {
        margin: '0 0 16px'
      }
    },
  },

  dropdown: {
    '& .MuiSelect-root': {
      minWidth: 'initial',
    }
  },

  sortPanel: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0 16px 0 0',
    marginBottom: 24,
    '& button': {
      minHeight: 24,
      minWidth: 24,
      font: fontDisplay('normal', theme.fontSize.base, 24),
    },
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      display: 'block',
      marginTop: 16,
    },
  },

  sortName: {
    width: 250,

    [theme.breakpoints.down(theme.breakpoints.values.sl)]: {
      width: 204,
    },
  },

  sortType: {
    ...flexCenter(),
    width: 120,
  },

  sortRisk: {
    ...flexCenter(),
    width: 120,
  },

  sortPricing: {
    ...flexCenter(),
    width: 100,
  },

  sortChart: {
    position: 'relative',
    top: 1,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 24,
    font: fontText('normal', theme.fontSize.base, 24),
    '& > $labelSelect': {
      paddingLeft: 22
    },

    '& > $roiSelect': {
      marginBottom: -1
    },

    '& .MuiSelect-select.MuiSelect-select': {
      [theme.breakpoints.between(theme.breakpoints.values.md, theme.breakpoints.values.sl)]: {
        paddingRight: 0
      },
    },

    [theme.breakpoints.down('sm')]: {
      padding: 0,
    },
  },

  labelSelect: {
    marginRight: 4,
    display: 'inline-block',
    font: fontText('normal', theme.fontSize.s, 16),
    textTransform: 'uppercase',
    color: theme.color.greys[80]
  },

  asc: {
    position: 'relative',

    '&:before': {
      ...arrowStyle,
      transform: 'rotate(180deg)',
    },
  },

  desc: {
    position: 'relative',

    '&:before': {
      ...arrowStyle
    }
  },

  chipWrapper: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    paddingTop: 24,

    '& > *': {
      marginRight: 4,
      marginBottom: 4
    },

  },

  filterButton: {
    border: `1px solid ${theme.color.greys[40]}`,
    borderRadius: 2,
    padding: 16,
    backgroundColor: theme.color.greys[0],
    margin: 0,
    height: 56,
  },

  searchBox: {
    flex: '327px 1 1',
    minWidth: ({ hasUnitCategorySelect }) => hasUnitCategorySelect ? 'initial' : 180,
    minHeight: 58,
    maxWidth: 327,
    alignSelf: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      minWidth: 296,
      maxWidth: 400,
    },
  },

  noResultsText: {
    marginTop: 20,
    font: fontText('normal', theme.fontSize.l, 24),
    color: theme.color.greys[50],
    textAlign: 'center',
  },

  skeletonListItem: {
    height: 103,
    marginBottom: 20,
    borderRadius: 8,
    [theme.breakpoints.down('sm')]: {
      height: 326,
    }
  },

  searchIcon: {
    width: 24,
    height: 24,
  },

  filterIcon: {},

  accordionTitleWrapper: {
    display: 'flex',
    cursor: 'initial',
    width: '100%',
    textTransform: 'initial',
    gap: 16,
    '& > div$searchBox': {
      flex: 1,
    },
    '& > div:not($chipBox)': {
      flex: ({ hasUnitCategorySelect}) => hasUnitCategorySelect ? 'initial' : 1,

      [theme.breakpoints.down('sm')]: {
        margin: '0 0 16px'
      }
    },
  },

  accordionTitle: {
    width: 'fit-content',
    [theme.breakpoints.down(theme.breakpoints.values.sl)]: {
      minWidth: 'initial',
    },
  },

  unitCategoryDropdownWrapper: {
    [theme.breakpoints.down('sm')]: {
      display: 'inline-flex',
      minWidth: 0,
    }
  },

  unitCategoryDropdown: {
    '& .MuiSelect-root.MuiSelect-select': {
      [theme.breakpoints.down(theme.breakpoints.values.sl)]: {
        minWidth: 'initial',
        width: 80
      },
      [theme.breakpoints.down('sm')]: {
        minWidth: '80%',
        width: 'auto'
      },
      [theme.breakpoints.down('xs')]: {
        minWidth: '70%',
      },
    },
  },

  accordionDetails: {
    padding: 0,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 24,
    alignItems: 'center',
    paddingTop: 24,
  },

  switchWrapper: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 0',

    '& > span': {
      color: theme.color.greys[80],
      font: fontText('normal', theme.fontSize.base, 20),
    }
  },

  filterTitle: {
    font: fontText('normal', theme.fontSize.xxl, 40),
    marginBottom: 24,
  },

  mobileFilterList: {
    listStyle: 'none',
    padding: 0,
    margin: '16px 0 0',
    borderTop: `1px solid ${theme.color.greys[20]}`,
    '& $checkboxWrapperItem': {
      '&:fist-child': {
        borderTop: `1px solid ${theme.color.greys[20]}`
      },
      borderBottom: `1px solid ${theme.color.greys[20]}`
    },
    '& $checkboxWrapperItemShow': {
      border: 'none',
    },
    '& $checkboxWrapperItemCheckboxAdditional': {
      border: 'none',
    }
  },

  mobileFilterButtonsBox: {
    '& > button': {
      width: '100%'
    },

    '& > button:last-of-type': {
      marginTop: 16
    }
  },

  mobilFilterInnerWrapper: {
    height: `calc(100% - 48px)`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  dropdownsAccordion: {
    display: 'flex',
    gap: 16,
    width: AccordionSummaryMaxWidth,
  },

  checkboxesWrapper: {
    display: 'flex',
    gap: 16,
    paddingTop: 16,
  },

  checkboxWrapperItem: {
    display: 'flex',
    alignItems: 'center',
  },

  checkboxWrapperItemShow: {
    border: 'none',
    padding: '24px 0',
  },

  checkboxWrapperItemCheckbox: {
    border: 'none',
    paddingBottom: 16,
  },

  checkboxWrapperItemCheckboxAdditional: {
    paddingTop: 8,
    paddingBottom: 8,
  },

  showLabel: {
    paddingBottom: 8,
  },

  autocompleteMobileButton: {
    width: ({ hasUnitCategorySelect}) => hasUnitCategorySelect ? '56px' : '100%',
    padding: 16,
    display: 'flex',
    alignItems: 'center',
    height: 56,
  },

  fundListFilterItem: {
    width: 'calc(25% - 12px)',
    '& .MuiFormControl-root': {
      width: '100%'
    }
  },

  checkboxesFilterItem: {
    width: 'auto',
    flex: 1,
  },

  accordionExpandAction: {
    display: 'flex',
    justifyContent: 'flex-start',
    paddingTop: 32,
    paddingLeft: 12,
    width: ExpandButtonWidth,
  },
  checkboxControlLabel: {
    marginBottom: 0,
  },
  dropdownsAccordionSmallItem: {
    width: 172,
  },
  filterPanelMobileContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  filterPanelMobileContentWithUnitCategorySelect: {
    paddingTop: 32,
  },
  roiSelect: {},
  autocomplete: {},
}), { name: 'FundList', index: StylesIndexInjections.OTHER });
